/**
 * Local storage manager for authentication state.
 * Handles JWT session tokens, user email, and player ID persistence.
 * Uses LocalSaveManager for cross-platform cookie storage.
 */

import { jwtDecode } from 'jwt-decode';
import { LocalSaveManager } from '../persistence/LocalSaveManager';
import { logger } from '../Analytics/Logger';

const AUTH_NAMESPACE = 'auth';
const AUTH_SESSION_KEY = 'session';

export interface CurrentUser {
  email: string;
  playerId: string;
  sessionToken: string;
}

interface JWTPayload {
  exp?: number;
  email?: string;
  playerId?: string;
}

/**
 * Get cookie expiration date from JWT token.
 * Returns JWT's exp time, or 1 year from now if exp is missing.
 */
function getExpirationFromToken(token: string): Date {
  try {
    const payload = jwtDecode<JWTPayload>(token);
    if (payload.exp) {
      // JWT exp is in seconds, convert to milliseconds
      return new Date(payload.exp * 1000);
    }
  } catch (error) {
    logger.error('[Auth] Failed to decode JWT for expiration:', error);
  }
  
  // Default to 1 year if we can't decode
  return new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
}

/**
 * Check if a JWT token is expired.
 * Returns true if token is expired or invalid.
 */
function isTokenExpired(token: string): boolean {
  try {
    const payload = jwtDecode<JWTPayload>(token);
    if (!payload.exp) {
      return true; // Missing expiration
    }
    
    // JWT exp is in seconds, Date.now() is in milliseconds
    const expirationTime = payload.exp * 1000;
    const currentTime = Date.now();
    
    return currentTime >= expirationTime;
  } catch (error) {
    logger.error('[Auth] Failed to decode JWT:', error);
    return true; // Invalid token
  }
}

/**
 * Check if the user is currently authenticated.
 * Returns true if a valid, non-expired session token exists.
 * Automatically clears session if token is expired.
 */
export function isAuthenticated(): boolean {
  const sessionData = LocalSaveManager.loadSimpleData<CurrentUser>(AUTH_NAMESPACE, AUTH_SESSION_KEY);
  const session = sessionData?.data;
  
  if (!session?.sessionToken) {
    return false;
  }
  
  // Check token expiration
  if (isTokenExpired(session.sessionToken)) {
    logger.info('[Auth] Session token expired, clearing session');
    clearSession();
    return false;
  }
  
  return true;
}

/**
 * Get the current authenticated user's details.
 * Returns null if not authenticated or token is expired.
 * Automatically clears session if token is expired.
 */
export function getCurrentUser(): CurrentUser | null {
  const sessionData = LocalSaveManager.loadSimpleData<CurrentUser>(AUTH_NAMESPACE, AUTH_SESSION_KEY);
  const session = sessionData?.data;

  if (!session?.sessionToken || !session?.email || !session?.playerId) {
    return null;
  }
  
  // Check token expiration
  if (isTokenExpired(session.sessionToken)) {
    logger.info('[Auth] Session token expired, clearing session');
    clearSession();
    return null;
  }

  return session;
}

/**
 * Get the session token for API requests.
 * Returns null if not authenticated or token is expired.
 * Automatically clears session if token is expired.
 */
export function getSessionToken(): string | null {
  const sessionData = LocalSaveManager.loadSimpleData<CurrentUser>(AUTH_NAMESPACE, AUTH_SESSION_KEY);
  const session = sessionData?.data;
  
  if (!session?.sessionToken) {
    return null;
  }
  
  // Check token expiration
  if (isTokenExpired(session.sessionToken)) {
    logger.info('[Auth] Session token expired, clearing session');
    clearSession();
    return null;
  }
  
  return session.sessionToken;
}

/**
 * Store session data using LocalSaveManager after successful authentication.
 * Uses 'auth' namespace to create cross-game auth cookies.
 * Cookie expiration matches the JWT's exp claim.
 */
export function setSession(sessionToken: string, email: string, playerId: string): void {
  const session: CurrentUser = { sessionToken, email, playerId };
  const expirationDate = getExpirationFromToken(sessionToken);
  LocalSaveManager.saveSimpleData(AUTH_NAMESPACE, AUTH_SESSION_KEY, session, expirationDate);
}

/**
 * Clear all session data from cookies.
 * Called on logout or session expiration.
 */
export function clearSession(): void {
  LocalSaveManager.clearSimpleData(AUTH_NAMESPACE, AUTH_SESSION_KEY);
}

export const authManager = {
  isAuthenticated,
  getCurrentUser,
  getSessionToken,
  setSession,
  clearSession
};
